import { deriveArchetype, deriveHeight, packLayout, deriveSize } from './core/layout';

// We will import measured once sync runs.
import { MEASURED as measure } from './measured.generated';

export const INTRO = {
  title: 'Solunary Architecture',
  whatItDoes: 'A comprehensive view of the Solunary ecosystem, from the React frontend down to the Python backend.',
  howItsBuilt: 'Built with React 19, FastAPI, and Stripe.'
};

export const GROUPS = [
{ id: 'frontend', label: 'Frontend Application' },
{ id: 'backend', label: 'Backend API & Services' },
{ id: 'ops', label: 'Admin & Operations' },
{ id: 'qa', label: 'Testing & QA' },
{ id: 'core', label: 'Core Infrastructure' }];


const nodeInputs = [
{
  id: 'frontend',
  groupId: 'frontend',
  whatItDoes: 'Base frontend setup and entry points.',
  howItsBuilt: 'CRA standard setup with some overrides.',
  role: 'react app base',
  files: ['frontend/src/App.js', 'frontend/src/index.js']
},
{
  id: 'frontend-src',
  groupId: 'frontend',
  whatItDoes: 'General React application code.',
  howItsBuilt: 'Hooks and context for app state.',
  role: 'frontend core',
  files: []
},
{
  id: 'frontend-components',
  groupId: 'frontend',
  whatItDoes: 'Reusable UI components.',
  howItsBuilt: 'Radix UI primitives mixed with Tailwind styling.',
  role: 'component library',
  files: ['frontend/src/components/ui/accordion.jsx']
},
{
  id: 'frontend-saas',
  groupId: 'frontend',
  whatItDoes: 'SaaS landing, signup, and marketing pages.',
  howItsBuilt: 'React pages with marketing copy.',
  role: 'saas storefront',
  files: ['frontend/src/saas/PlatformLanding.jsx']
},
{
  id: 'frontend-public',
  groupId: 'frontend',
  whatItDoes: 'Public facing tenant portals and scheduling interfaces.',
  howItsBuilt: 'React views interacting with backend booking APIs.',
  role: 'client portal',
  files: ['frontend/src/public/ClientPortal.jsx']
},
{
  id: 'frontend-templates',
  groupId: 'frontend',
  whatItDoes: 'Website templates for tenants.',
  howItsBuilt: 'Themed React components (Luxe, Clinical, etc).',
  role: 'tenant themes',
  files: ['frontend/src/templates/BoldTemplate.jsx']
},
{
  id: 'frontend-admin',
  groupId: 'ops',
  whatItDoes: 'Admin dashboard for tenants to manage their business.',
  howItsBuilt: 'Data-heavy React views calling backend CRM/payments.',
  role: 'tenant admin',
  files: ['frontend/src/admin/AdminLayout.jsx']
},
{
  id: 'frontend-superadmin',
  groupId: 'ops',
  whatItDoes: 'Platform-level super admin interface.',
  howItsBuilt: 'React views for managing tenants and billing.',
  role: 'platform admin',
  files: ['frontend/src/super_admin/TenantDisplay.js']
},
{
  id: 'frontend-tenant',
  groupId: 'frontend',
  whatItDoes: 'Tenant routing and context resolution.',
  howItsBuilt: 'Subdomain or path-based tenant resolution.',
  role: 'tenant router',
  files: ['frontend/src/tenant/TenantSite.jsx']
},
{
  id: 'frontend-i18n',
  groupId: 'frontend',
  whatItDoes: 'Internationalization and translations.',
  howItsBuilt: 'Custom i18n hooks or libraries.',
  role: 'i18n',
  files: ['frontend/src/i18n/index.js']
},
{
  id: 'frontend-routes',
  groupId: 'frontend',
  whatItDoes: 'Client-side routing definitions.',
  howItsBuilt: 'React Router v7 setup.',
  role: 'router',
  files: ['frontend/src/routes/adminRoutes.jsx']
},
{
  id: 'frontend-lib',
  groupId: 'core',
  whatItDoes: 'Frontend utilities and API clients.',
  howItsBuilt: 'Axios instance and shared helpers.',
  role: 'api client',
  files: ['frontend/src/lib/api.js']
},
{
  id: 'frontend-plugins',
  groupId: 'core',
  whatItDoes: 'Frontend build plugins and health checks.',
  howItsBuilt: 'Webpack/craco extensions.',
  role: 'build plugins',
  files: ['frontend/plugins/health-check/health-endpoints.js']
},
{
  id: 'frontend-tests',
  groupId: 'qa',
  whatItDoes: 'Frontend unit and static tests.',
  howItsBuilt: 'Jest test suites.',
  role: 'frontend tests',
  files: []
},
{
  id: 'backend',
  groupId: 'backend',
  whatItDoes: 'Core FastAPI application and routes.',
  howItsBuilt: 'Python FastAPI with core endpoints.',
  role: 'api server',
  files: ['backend/auth_handoff.py']
},
{
  id: 'backend-emails',
  groupId: 'backend',
  whatItDoes: 'Email rendering and delivery.',
  howItsBuilt: 'Templates and SMTP transport logic.',
  role: 'mail sender',
  files: ['backend/emails/outbox.py']
},
{
  id: 'backend-modules',
  groupId: 'backend',
  whatItDoes: 'Various backend business logic modules.',
  howItsBuilt: 'Modular Python packages (CRM, AI agent).',
  role: 'business logic',
  files: ['backend/modules/ai_agent.py']
},
{
  id: 'backend-booking',
  groupId: 'backend',
  whatItDoes: 'Scheduling, overlapping, and calendar sync.',
  howItsBuilt: 'Complex logic integrating with Google Calendar.',
  role: 'booking engine',
  files: ['backend/modules/booking/gcal_reconcile.py']
},
{
  id: 'backend-payments',
  groupId: 'backend',
  whatItDoes: 'Payment gateway integration and receipts.',
  howItsBuilt: 'Stripe or external payment gateway wrappers.',
  role: 'payment gateway',
  files: ['backend/modules/payments/gateway.py']
},
{
  id: 'backend-tests',
  groupId: 'qa',
  whatItDoes: 'Backend pytest suite.',
  howItsBuilt: 'Pytest with DB fixtures.',
  role: 'backend tests',
  files: ['backend/tests/gcal_fixtures.py']
},
{
  id: 'scripts',
  groupId: 'ops',
  whatItDoes: 'Maintenance and operational scripts.',
  howItsBuilt: 'Python shell scripts for cleanup/dedup.',
  role: 'ops scripts',
  files: ['scripts/cleanup_test_tenants.py']
},
{
  id: 'scripts-qa',
  groupId: 'qa',
  whatItDoes: 'Browser automation and E2E scripts.',
  howItsBuilt: 'Playwright or Selenium scripts.',
  role: 'e2e scripts',
  files: ['scripts/browser_qa/otp_booking_gate.py']
},
{
  id: 'test-reports',
  groupId: 'qa',
  whatItDoes: 'Generated QA and test reports.',
  howItsBuilt: 'Static files dumped by CI.',
  role: 'reports',
  files: []
},
{
  id: 'root',
  groupId: 'core',
  whatItDoes: 'Repository configuration files.',
  howItsBuilt: 'Docker compose, readmes, configs.',
  role: 'repo config',
  files: ['docker-compose.yml']
}];


// Now we derive geometry from measurements
export const NODES = nodeInputs.map((n) => {
  const m = measure[n.id] || { count: 0, loc: 0 };
  const { archetype, params } = deriveArchetype(m);
  return {
    ...n,
    archetype,
    params,
    size: deriveSize(archetype, params, m),
    height: deriveHeight(m)
  };
});

// The nodes need a `group` property for SidePanels and IsoCanvas to work
for (const n of NODES) {
  n.group = n.groupId;
}

// Add authored footprints if needed, else pack automatically.
const footprints = packLayout(
  NODES.map((n) => ({
    item: n.id,
    group: n.group,
    size: n.size
  })),
  GROUPS.map((g) => g.id)
);

for (const n of NODES) {
  n.footprint = footprints.get(n.id) || { gx: 0, gy: 0, w: 2, d: 2 };
  if (!n.name) n.name = n.id;
  if (!n.code) n.code = n.id.substring(0, 3).toUpperCase();
}
export const EDGES = [
{ id: 'e1', from: 'frontend-lib', to: 'backend', kind: 'call', label: 'API requests' },
{ id: 'e2', from: 'frontend-saas', to: 'frontend-lib', kind: 'call', label: 'Uses API client' },
{ id: 'e3', from: 'frontend-public', to: 'frontend-lib', kind: 'call', label: 'Uses API client' },
{ id: 'e4', from: 'frontend-admin', to: 'frontend-lib', kind: 'call', label: 'Uses API client' },
{ id: 'e5', from: 'frontend-superadmin', to: 'frontend-lib', kind: 'call', label: 'Uses API client' },
{ id: 'e6', from: 'backend', to: 'backend-booking', kind: 'call', label: 'Processes booking' },
{ id: 'e7', from: 'backend', to: 'backend-payments', kind: 'call', label: 'Processes payment' },
{ id: 'e8', from: 'backend', to: 'backend-emails', kind: 'call', label: 'Queues email' },
{ id: 'e9', from: 'backend-booking', to: 'backend-emails', kind: 'call', label: 'Sends confirmation' }];


export const FLOWS = [
{
  id: 'f1',
  name: 'Tenant Booking',
  payload: 'Appointment',
  route: ['e3', 'e1', 'e6', 'e9']
},
{
  id: 'f2',
  name: 'Admin Dashboard',
  payload: 'TenantData',
  route: ['e4', 'e1']
},
{
  id: 'f3',
  name: 'Payment Processing',
  payload: 'Transaction',
  route: ['e1', 'e7']
}];